import React from "react";
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { markerStroke, scribbleLoop } from "../../tripmovie/sequences/introDrawing";
import { C, HEAD, BODY } from "./brand";
import { swoosh } from "./draw";
import { eio, ramp, type Lay } from "./kit";
import { Media, type MediaSrc } from "./markers";
import { LOGO_MARK } from "./logo.generated";

// The headlines: Poppins 600 and a green second line, alive. Words sharpen out of a blur,
// a clip pill opens inside the sentence and pushes the words apart, and the hand-drawn loop from the
// trip reel circles the word that matters.

export type Tok = string | { pill: MediaSrc; w: number } | { check: true };

const PINK = "#e84d7e";
let ctx: CanvasRenderingContext2D | null = null;
export const measure = (text: string, size: number, weight = 600) => {
	if (!ctx) ctx = document.createElement("canvas").getContext("2d");
	ctx!.font = `${weight} ${size}px Poppins`;
	return ctx!.measureText(text).width - size * 0.022 * text.length;
};

export const Title: React.FC<{
	lay: Lay;
	at: number;
	out: number;
	lines: [Tok[], Tok[]];
	sub?: string;
	eyebrow?: string;
	sticker?: boolean; // the eyebrow as a tilted green sticker with the Stippl mark
	scribble?: { line: 0 | 1; word: number; at?: number; seed?: number };
	underline?: { line: 0 | 1; from: number; to: number; at?: number };
	stamp?: boolean;
	taps?: { line: 0 | 1; word: number; at: number };
	pos?: { x?: number; top: number; align: "left" | "center"; size?: number; sub?: number };
}> = ({ lay, at, out, lines, sub, eyebrow, sticker, scribble, underline, stamp, taps, pos }) => {
	const f = useCurrentFrame();
	const { fps } = useVideoConfig();
	if (f < at - 2 || f > out + 14) return null;
	const T = lay.text;
	const size = pos?.size ?? T.size;
	const align = pos?.align ?? T.align;
	const left = pos?.x ?? T.x;
	const top0 = pos?.top ?? T.top;
	const subSize = pos?.sub ?? T.sub;
	const oOut = 1 - ramp(f, out, out + 10, eio);
	const lift = -30 * ramp(f, out, out + 10, eio);
	const blurOut = 10 * ramp(f, out, out + 10, eio);
	const space = measure(" ", size);
	const stickH = T.eyebrow * 2.9;
	const eyeH = eyebrow ? (sticker ? stickH + size * 0.34 : T.eyebrow * 1.2 + size * 0.36) : 0;
	const lineH = size * 1.12;

	// stamp: the first line lands like a rubber stamp and the block shakes once
	const stampS = stamp ? spring({ frame: f - at, fps, config: { damping: 9, stiffness: 260, mass: 0.7 } }) : 1;
	const shake = stamp && f > at + 5 && f < at + 18 ? Math.sin((f - at) * 2.4) * 9 * (1 - (f - at - 5) / 13) : 0;

	let wordIndex = 0;
	const words: React.ReactNode[] = [];
	const geo: { line: number; i: number; x: number; w: number; y: number }[] = [];
	lines.forEach((toks, li) => {
		const lineTop = top0 + eyeH + li * lineH;
		// widths first, pills at their current size
		const widths = toks.map((t, ti) => {
			if (typeof t === "string") return measure(t, size);
			if ("pill" in t) {
				const g = spring({ frame: f - (at + 8 + ti * 2), fps, config: { damping: 13, stiffness: 150 } });
				return t.w * size * g;
			}
			const g = spring({ frame: f - (at + 14), fps, config: { damping: 10, stiffness: 220 } });
			return size * 0.78 * g;
		});
		const total = widths.reduce((a, b) => a + b, 0) + space * (toks.length - 1);
		let x = align === "center" ? (lay.W - total) / 2 : left;
		toks.forEach((t, ti) => {
			const w = widths[ti];
			const color = li === 0 ? C.ink : C.green;
			if (typeof t === "string") {
				const wi = wordIndex++;
				const s = stamp && li === 0 ? stampS : spring({ frame: f - (at + wi * 3), fps, config: { damping: 13, stiffness: 170, mass: 0.7 } });
				const pre = f < at + wi * 3 && !(stamp && li === 0);
				words.push(
					<span
						key={`${li}-${ti}`}
						style={{
							position: "absolute",
							left: x,
							top: lineTop,
							fontFamily: HEAD,
							fontWeight: 600,
							fontSize: size,
							lineHeight: 1.12,
							letterSpacing: "-0.022em",
							color,
							whiteSpace: "nowrap",
							opacity: pre ? 0 : Math.min(1, s * 1.6),
							filter: stamp && li === 0 ? undefined : `blur(${Math.max(0, (1 - s) * 14)}px)`,
							transform:
								stamp && li === 0
									? `scale(${interpolate(s, [0, 1], [2.6, 1])}) rotate(${interpolate(s, [0, 1], [-10, 0])}deg)`
									: `translateY(${(1 - s) * size * 0.45}px) scale(${0.9 + 0.1 * s})`,
							transformOrigin: "0% 70%",
						}}
					>
						{t}
					</span>
				);
				geo.push({ line: li, i: ti, x, w, y: lineTop });
			} else if ("pill" in t) {
				if (w > 1) {
					const h = size * 0.86;
					words.push(
						<span
							key={`${li}-${ti}`}
							style={{
								position: "absolute",
								left: x,
								top: lineTop + size * 0.2,
								width: w,
								height: h,
								borderRadius: 999,
								overflow: "hidden",
								border: `${Math.max(3, size * 0.05)}px solid #fff`,
								boxShadow: "0 8px 22px rgba(15,62,74,.22)",
								boxSizing: "border-box",
								transform: `rotate(${-3 + Math.sin(f / 20) * 1.2}deg)`,
								background: C.band,
							}}
						>
							<Media m={t.pill} from={at + 8} />
						</span>
					);
				}
				geo.push({ line: li, i: ti, x, w, y: lineTop });
			} else {
				if (w > 1) {
					const d = size * 0.66;
					words.push(
						<span
							key={`${li}-${ti}`}
							style={{
								position: "absolute",
								left: x + (w - d) / 2,
								top: lineTop + size * 0.3,
								width: d,
								height: d,
								borderRadius: d,
								background: C.greenDeep,
								boxShadow: "0 8px 20px rgba(17,180,132,.35)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								transform: `scale(${w / (size * 0.78)})`,
							}}
						>
							<svg width={d * 0.6} height={d * 0.6} viewBox="0 0 24 24">
								<path
									d="M5 12.5l4.2 4.2L19 7"
									fill="none"
									stroke="#fff"
									strokeWidth={3}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeDasharray={24}
									strokeDashoffset={24 * (1 - ramp(f, at + 18, at + 28))}
								/>
							</svg>
						</span>
					);
				}
				geo.push({ line: li, i: ti, x, w, y: lineTop });
			}
			x += w + space;
		});
	});

	// the hand-drawn loop around the word that matters
	let loop: React.ReactNode = null;
	if (scribble) {
		const g = geo.find((q) => q.line === scribble.line && q.i === scribble.word);
		const sAt = scribble.at ?? at + 22;
		const p = ramp(f, sAt, sAt + 16, eio);
		if (g && p > 0) {
			const pts = scribbleLoop(g.x + g.w / 2, g.y + size * 0.62, g.w / 2 + size * 0.3, size * 0.56, -4, scribble.seed ?? 11);
			const shown = pts.slice(0, Math.max(2, Math.ceil(pts.length * p)));
			loop = <path d={markerStroke(shown, size * 0.08)} fill={PINK} />;
		}
	}
	let line: React.ReactNode = null;
	if (underline) {
		const a = geo.find((q) => q.line === underline.line && q.i === underline.from);
		const b = geo.find((q) => q.line === underline.line && q.i === underline.to);
		const uAt = underline.at ?? at + 22;
		const p = ramp(f, uAt, uAt + 14, eio);
		if (a && b && p > 0) {
			const pts = swoosh(a.x - size * 0.05, b.x + b.w + size * 0.08, a.y + size * 1.1);
			line = <path d={markerStroke(pts.slice(0, Math.max(2, Math.ceil(pts.length * p))), size * 0.09)} fill={PINK} />;
		}
	}
	let ripples: React.ReactNode = null;
	if (taps) {
		const g = geo.find((q) => q.line === taps.line && q.i === taps.word);
		if (g) {
			ripples = [0, 10].map((d) => {
				const t = ramp(f, taps.at + d, taps.at + d + 16);
				if (f < taps.at + d || t >= 1) return null;
				return (
					<circle
						key={d}
						cx={g.x + g.w * 0.62}
						cy={g.y + size * 0.62}
						r={size * (0.3 + 0.9 * t)}
						fill="none"
						stroke={C.green}
						strokeWidth={size * 0.05 * (1 - t)}
						opacity={1 - t}
					/>
				);
			});
		}
	}

	const subT = spring({ frame: f - (at + 14), fps, config: { damping: 16, stiffness: 140 } });
	const eyeT = ramp(f, at - 2, at + 10);
	const subTop = top0 + eyeH + 2 * lineH + size * 0.34;
	return (
		<div
			style={{
				position: "absolute",
				inset: 0,
				opacity: oOut,
				transform: `translate(${shake}px, ${lift}px)`,
				filter: blurOut ? `blur(${blurOut}px)` : undefined,
				zIndex: 50,
			}}
		>
			{eyebrow &&
				sticker &&
				(() => {
					const st = spring({ frame: f - (at - 4), fps, config: { damping: 9, stiffness: 200, mass: 0.7 } });
					const shine = ramp(f, at + 10, at + 26);
					return (
						<div style={{ position: "absolute", top: top0, left: align === "center" ? 0 : left, right: align === "center" ? 0 : undefined, textAlign: align }}>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: stickH * 0.22,
									height: stickH,
									padding: `0 ${stickH * 0.5}px 0 ${stickH * 0.36}px`,
									borderRadius: 999,
									background: "#11B484",
									boxShadow: `0 ${stickH / 12}px ${stickH / 2}px rgba(17,180,132,.35)`,
									color: "#fff",
									fontFamily: HEAD,
									fontWeight: 700,
									fontSize: stickH * 0.4,
									letterSpacing: "0.1em",
									position: "relative",
									overflow: "hidden",
									transformOrigin: align === "center" ? "50% 50%" : "0% 50%",
									transform: `scale(${0.4 + 0.6 * st}) rotate(${-3 - (1 - st) * 10}deg)`,
									opacity: Math.min(1, st * 2),
								}}
							>
								<svg height={stickH * 0.52} width={(stickH * 0.52 * 23) / 30} viewBox="0 0 23 30">
									{LOGO_MARK.map((d, i) => (
										<path key={i} d={d} fill="#fff" />
									))}
								</svg>
								{eyebrow}
								<span
									style={{
										position: "absolute",
										top: 0,
										bottom: 0,
										width: stickH * 0.8,
										left: `${-30 + 160 * shine}%`,
										background: "linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.55) 50%, rgba(255,255,255,0) 100%)",
										opacity: shine > 0 && shine < 1 ? 1 : 0,
									}}
								/>
							</div>
						</div>
					);
				})()}
			{eyebrow && !sticker && (
				<div
					style={{
						position: "absolute",
						top: top0,
						left: align === "center" ? 0 : left,
						right: align === "center" ? 0 : undefined,
						textAlign: align,
						fontFamily: HEAD,
						fontWeight: 600,
						fontSize: T.eyebrow,
						letterSpacing: "0.14em",
						color: C.greenText,
						opacity: eyeT,
						transform: `translateY(${(1 - eyeT) * 12}px)`,
					}}
				>
					<span
						style={{
							display: "inline-block",
							width: T.eyebrow * 0.5,
							height: T.eyebrow * 0.5,
							borderRadius: 99,
							background: C.green,
							marginRight: 12,
							verticalAlign: "middle",
						}}
					/>
					{eyebrow}
				</div>
			)}
			<svg style={{ position: "absolute", inset: 0, overflow: "visible", zIndex: 2 }} width={lay.W} height={lay.H}>
				{loop}
				{line}
				{ripples}
			</svg>
			{words}
			{sub && (
				<div
					style={{
						position: "absolute",
						top: subTop,
						left: align === "center" ? lay.W * 0.08 : left,
						width: align === "center" ? lay.W * 0.84 : 640,
						textAlign: align,
						fontFamily: BODY,
						fontSize: subSize,
						lineHeight: 1.45,
						color: C.muted,
						opacity: Math.min(1, subT * 1.5),
						filter: `blur(${Math.max(0, (1 - subT) * 8)}px)`,
						transform: `translateY(${(1 - subT) * 16}px)`,
					}}
				>
					{sub}
				</div>
			)}
		</div>
	);
};
